// Framework-neutral map model shared by the React map component and the
// server-side (Lambda) email renderer, so the on-screen map and the emailed
// map image are always drawn from a single source of truth.

export const TITLE_SUFFIX = 'Medicaid Invoice Availability';
export const DISCLAIMER =
  "This map may not precisely reflect your organization's data as there can be variability between manufacturers.";
export const DISCLAIMER_SUBSET =
  'Some states marked as available may have only released a subset of their invoices. See below for invoice-level tracking.';

// Canonical US hex-tile layout: x = row (0 top .. 8 bottom), y = column (0 left .. 15 right).
export const US_LAYOUT = [
  ['AL', 6, 7],
  ['AK', 0, 0],
  ['AZ', 5, 3],
  ['AR', 5, 6],
  ['CA', 5, 2],
  ['CO', 4, 3],
  ['CT', 3, 11],
  ['DE', 4, 9],
  ['DC', 4, 10],
  ['FL', 8, 8],
  ['GA', 7, 8],
  ['HI', 8, 0],
  ['ID', 3, 2],
  ['IL', 3, 6],
  ['IN', 3, 7],
  ['IA', 3, 5],
  ['KS', 5, 5],
  ['KY', 4, 6],
  ['LA', 6, 5],
  ['ME', 0, 11],
  ['MD', 4, 8],
  ['MA', 2, 10],
  ['MI', 2, 7],
  ['MN', 2, 4],
  ['MS', 6, 6],
  ['MO', 4, 5],
  ['MT', 2, 2],
  ['NE', 4, 4],
  ['NV', 4, 2],
  ['NH', 1, 11],
  ['NJ', 3, 10],
  ['NM', 6, 3],
  ['NY', 2, 9],
  ['NC', 5, 9],
  ['ND', 2, 3],
  ['OH', 3, 8],
  ['OK', 6, 4],
  ['OR', 4, 1],
  ['PA', 3, 9],
  ['PR', 8, 11],
  ['RI', 2, 11],
  ['SC', 6, 8],
  ['SD', 3, 4],
  ['VI', 8, 12],
  ['TN', 5, 7],
  ['TX', 7, 4],
  ['UT', 5, 4],
  ['GU', 8, 13],
  ['VT', 1, 10],
  ['VA', 5, 8],
  ['WA', 2, 1],
  ['AS', 8, 14],
  ['WV', 4, 7],
  ['WI', 2, 5],
  ['WY', 3, 3],
  ['MP', 8, 15],
];

export const COL = 52; // horizontal spacing between columns (y)
export const ROW = 45; // vertical spacing between rows (x) = 3/4 of full hex height
export const STAGGER = 26; // odd-row horizontal offset (COL / 2); rows shift left to nest deeply
export const MX = 45; // left margin
export const MY = 45; // top margin
export const TILE_SCALE = 0.94; // small gap between tiles; keeps deep nesting

// Pointy-top hexagon half-dimensions for a flat-to-flat width of COL.
export const HVP = 30; // half height to the top/bottom points
export const HVE = 15; // half height to the flat-edge corners
export const HHF = 26; // half width to the left/right flat edges

export const COLOR_ARRIVED_START = '#3b82f6'; // Tailwind blue-500
export const COLOR_ARRIVED_END = '#2563eb'; // Tailwind blue-600 (matches site buttons)
export const COLOR_PENDING = '#52525b';
export const COLOR_NOT_TRACKED = '#263238';
export const BORDER = '#2a2f36';

// Territories shown on the map but excluded from the arrived percentage.
export const NOT_TRACKED = new Set(['AS', 'GU', 'VI', 'MP']);

export const MAP_VIEWBOX = '-110 10 960 440';

export function center(x, y) {
  return {
    cx: MX + COL * y - (x % 2 ? STAGGER : 0),
    cy: MY + ROW * x,
  };
}

// Six vertices of a pointy-top hexagon centred at (cx, cy), scaled by s.
export function hexPoints(cx, cy, s = 1) {
  const g = s * TILE_SCALE;
  const vp = HVP * g;
  const ve = HVE * g;
  const hf = HHF * g;
  return [
    [cx, cy - vp],
    [cx + hf, cy - ve],
    [cx + hf, cy + ve],
    [cx, cy + vp],
    [cx - hf, cy + ve],
    [cx - hf, cy - ve],
  ]
    .map((p) => p.join(','))
    .join(' ');
}

// Derive tiles + overall percentage + quarter from raw data.json.
export function computeMap(data) {
  const arrivedSet = new Set(
    (data?.arrived ?? []).map((code) => String(code).toUpperCase()),
  );
  let trackedCount = 0;
  let arrivedCount = 0;
  const tiles = US_LAYOUT.map(([code, x, y]) => {
    const isTracked = !NOT_TRACKED.has(code);
    const hasArrived = isTracked && arrivedSet.has(code);
    if (isTracked) trackedCount += 1;
    if (hasArrived) arrivedCount += 1;
    return { code, x, y, arrived: hasArrived, tracked: isTracked };
  });
  const percent =
    trackedCount === 0 ? 0 : Math.round((arrivedCount / trackedCount) * 100);
  return {
    tiles,
    percent,
    quarter: data?.quarter ?? '',
    lastRefreshed: data?.last_refreshed_instant ?? '',
  };
}

// Format a database instant into a readable label. Timezone-less values (e.g.
// "2026-08-11T18:00:11.183") are treated as UTC, matching how the DB stores them.
export function formatRefreshed(instant, timeZone) {
  if (!instant) return '';
  let s = String(instant).trim().replace(' ', 'T');
  // If no timezone offset is present, assume UTC.
  if (!/([zZ]|[+-]\d{2}:?\d{2})$/.test(s)) {
    s += 'Z';
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const opts = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  };
  if (timeZone) opts.timeZone = timeZone;
  return d.toLocaleString('en-US', opts);
}

function tileFill(tile) {
  if (!tile.tracked) return COLOR_NOT_TRACKED;
  if (tile.arrived) return 'url(#arrivedGradient)';
  return COLOR_PENDING;
}

// One hexagon as an SVG string. `label` renders the two-line overall badge.
function hexSvg({ x, y, fill, code, scale = 1, fontSize = 16, label }) {
  const { cx, cy } = center(x, y);
  const points = hexPoints(cx, cy, scale);
  const polygon = `<polygon points="${points}" fill="${fill}" stroke="${BORDER}" stroke-width="1.5" />`;
  let text;
  if (label) {
    text =
      `<text x="${cx}" y="${cy}" text-anchor="middle" fill="#ffffff">` +
      `<tspan x="${cx}" dy="-0.1em" font-size="${fontSize}" font-weight="bold">${label.top}</tspan>` +
      `<tspan x="${cx}" dy="1.3em" font-size="${fontSize * 0.5}" font-weight="600">${label.bottom}</tspan>` +
      `</text>`;
  } else {
    text =
      `<text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central" ` +
      `fill="#ffffff" font-size="${fontSize}" font-weight="bold">${code}</text>`;
  }
  return `<g>${polygon}${text}</g>`;
}

/**
 * Build the map as a standalone SVG string (used server-side to rasterize to
 * PNG for the daily email). Mirrors the on-screen map exactly.
 */
export function buildMapSvg(
  data,
  {
    background = null,
    viewBox = MAP_VIEWBOX,
    title = null,
    beta = false,
    footerLeft = null,
  } = {},
) {
  const { tiles, percent } = computeMap(data);

  const defs =
    `<defs><linearGradient id="arrivedGradient" x1="0" y1="0" x2="0" y2="1">` +
    `<stop offset="0" stop-color="${COLOR_ARRIVED_START}" />` +
    `<stop offset="1" stop-color="${COLOR_ARRIVED_END}" />` +
    `</linearGradient></defs>`;

  const [vbX, vbY, vbW] = viewBox.split(/\s+/).map(Number);
  const vbH = Number(viewBox.split(/\s+/)[3]);
  const bg =
    background ?
      `<rect x="${vbX}" y="${vbY}" width="${vbW}" height="${vbH}" fill="${background}" />`
    : '';

  let titleSvg = '';
  if (title) {
    const fs = 34;
    const yB = vbY + 46;
    const cx = vbX + vbW / 2;
    // Approximate title width for Inter Bold to lay out an inline BETA badge.
    const titleW = title.length * fs * 0.52;
    const badgeW = 62;
    const badgeH = 26;
    const gap = 16;
    const totalW = titleW + (beta ? gap + badgeW : 0);
    const x0 = cx - totalW / 2;
    titleSvg =
      `<text x="${x0}" y="${yB}" text-anchor="start" ` +
      `fill="#ffffff" font-size="${fs}" font-weight="bold">${title}</text>`;
    if (beta) {
      const bx = x0 + titleW + gap;
      const by = yB - 24;
      titleSvg +=
        `<rect x="${bx}" y="${by}" width="${badgeW}" height="${badgeH}" rx="13" ` +
        `fill="#eab308" fill-opacity="0.2" stroke="#eab308" stroke-width="1.5" />` +
        `<text x="${bx + badgeW / 2}" y="${by + badgeH / 2 + 5.5}" ` +
        `text-anchor="middle" fill="#fde047" font-size="15" font-weight="600">BETA</text>`;
    }
  }

  const footerSvg =
    footerLeft ?
      `<text x="${vbX + 8}" y="${vbY + vbH - 12}" text-anchor="start" ` +
      `fill="#9ca3af" font-size="15">${footerLeft}</text>`
    : '';

  const stateTiles = tiles
    .map((t) => hexSvg({ x: t.x, y: t.y, fill: tileFill(t), code: t.code }))
    .join('');

  const overall = hexSvg({
    x: 4,
    y: 13,
    fill: 'url(#arrivedGradient)',
    scale: 2,
    fontSize: 30,
    label: { top: `${percent}%`, bottom: 'Overall' },
  });

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" ` +
    `font-family="Inter, ui-sans-serif, system-ui, sans-serif">` +
    `${bg}${defs}${titleSvg}${stateTiles}${overall}${footerSvg}</svg>`
  );
}
